import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import matter from "gray-matter";
import Markdown from "markdown-to-jsx";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import DefaultNavbar from "custom/Navbars/DefaultNavbar";
import DefaultFooter from "custom/Footers/DefaultFooter";
import routes from "routes";
import footerRoutes from "footer.routes";
import bgImage from "assets/images/brand_sm.png";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { TwitterTimelineEmbed, TwitterShareButton, TwitterFollowButton, TwitterHashtagButton, TwitterMentionButton, TwitterTweetEmbed, TwitterMomentShare, TwitterDMButton, TwitterVideoEmbed, TwitterOnAirButton } from 'react-twitter-embed';

// Función para obtener el contenido del post desde GitHub
const fetchPost = async (postId, setPost, setError) => {
  try {
    const repoOwner = "pwncat-offsec";
    const repoName = "Blog";
    const apiUrl = `https://api.github.com/repos/${repoOwner}/${repoName}/contents/${postId}.md`;

    const response = await fetch(apiUrl);
    if (!response.ok) {
      throw new Error(`La solicitud a la API de GitHub falló: ${response.statusText}`);
    }

    const file = await response.json();
    const markdownWithMeta = Buffer.from(file.content, 'base64').toString('utf-8');
    const { data: frontMatter, content } = matter(markdownWithMeta);

    setPost({ frontMatter, content });
  } catch (error) {
    console.error("No se pudo cargar el post:", error);
    setError(true);
  }
};

const CopyCodeButton = ({ code, onCopy }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    onCopy();
  };

  return (
    <IconButton
      onClick={handleCopy}
      sx={{
        position: "absolute",
        top: "0.5rem",
        right: "0.5rem",
        color: "white",
        width: "24px",
        height: "24px",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.4)",
        },
        zIndex: 2, // Asegura que el botón esté en la parte superior
        borderRadius: "4px", // Hace que el botón sea cuadrado
      }}
    >
      <ContentCopyIcon color="white" sx={{ fontSize: "16px" }} />
    </IconButton>
  );
};

const PostPage = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchPostCallback = useCallback(() => {
    fetchPost(postId, setPost, setError);
  }, [postId]);

  useEffect(() => {
    fetchPostCallback();
  }, [fetchPostCallback]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCopy = () => {
    setSnackbarOpen(true);
  };

  if (error) {
    return (
      <Container>
        <Alert severity="error">No se pudo cargar el post. Por favor, inténtelo de nuevo más tarde.</Alert>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "external",
          route: "/support/contact-us",
          label: "Comenzar",
          color: "info",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="40vh"
        width="100%"
        sx={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography variant="h3" color="white">
              {post.frontMatter.title}
            </MKTypography>
            <MKTypography variant="body1" color="white" mt={1}>
              {post.frontMatter.description}
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card sx={{ p: 2, mx: { xs: 2, lg: 3 }, mt: -8, mb: 4 }}>
        <MKBox component="section">
          <Container>
            <Grid container spacing={3} sx={{ mt: 3 }}>
              <Grid item xs={12}>
                <MKTypography variant="body2" color="text" mt={1}>
                  {post.frontMatter.date} - {post.frontMatter.author} ({post.frontMatter.nick})
                </MKTypography>
                <MKBox mt={3}>
                  <Markdown
                    options={{
                      overrides: {
                        h1: {
                          component: MKTypography,
                          props: {
                            variant: "h4",
                            gutterBottom: true,
                            style: { marginTop: "2rem", marginBottom: "1rem", textAlign: "justify" },
                          },
                        },
                        h2: {
                          component: MKTypography,
                          props: {
                            variant: "h5",
                            gutterBottom: true,
                            style: { marginTop: "1.75rem", marginBottom: "1rem", textAlign: "justify" },
                          },
                        },
                        h3: {
                          component: MKTypography,
                          props: {
                            variant: "h6",
                            gutterBottom: true,
                            style: { marginTop: "1.5rem", marginBottom: "1rem", textAlign: "justify" },
                          },
                        },
                        p: {
                          component: MKTypography,
                          props: {
                            variant: "body2",
                            paragraph: true,
                            sx: { textAlign: "justify" },
                            style: { marginTop: "1rem", marginBottom: "1rem" },
                          },
                        },
                        a: {
                          component: MKTypography,
                          props: {
                            variant: "body2",
                            color: "primary",
                            sx: {
                              textDecoration: "underline",
                              fontWeight: "bold",
                              cursor: "pointer",
                              display: "inline",
                              textAlign: "justify",
                            },
                          },
                        },
                        strong: {
                          component: MKTypography,
                          props: {
                            variant: "body2",
                            component: "span",
                            sx: { fontWeight: "bold", display: "inline", textAlign: "justify" },
                          },
                        },
                        span: {
                          component: MKTypography,
                          props: {
                            variant: "body2",
                            component: "span",
                          },
                        },
                        li: {
                          component: (props) => (
                            <li style={{ fontSize: "1rem", fontWeight: "bold", display: "list-item", marginBottom: "1em", textAlign: "justify" }}>
                              <span style={{ display: "inline" }}>
                                <MKTypography variant="body2" component="span" display="block" {...props} />
                              </span>
                            </li>
                          ),
                        },
                        ul: {
                          component: (props) => (
                            <ul style={{ paddingLeft: "1.5rem", fontSize: "1rem", listStyleType: "disc", listStylePosition: "outside", marginTop: "1em", marginBottom: "1em", marginLeft: "3em",paddingLeft: 0, textAlign: "justify" }} {...props} />
                          ),
                        },
                        ol: {
                          component: (props) => (
                            <ol style={{ paddingLeft: "1.5rem", fontSize: "1rem", listStyleType: "decimal", listStylePosition: "outside", marginTop: "1em", marginBottom: "1em", marginLeft: "3em", paddingLeft: 0, textAlign: "justify" }} {...props} />
                          ),
                        },
                        code: {
                          component: ({ className, children }) => {
                            const language = className?.replace("lang-", "") || "";
                            const code = children.trim();
                            if (language) {
                              return (
                                <MKBox sx={{ position: "relative", my: 2 }}>
                                  <SyntaxHighlighter
                                    style={materialDark}
                                    language={language}
                                    customStyle={{ maxWidth: "100%", margin: "0 auto", padding: "1em", borderRadius: "4px", fontSize: "0.875rem" }}
                                  >
                                    {code}
                                  </SyntaxHighlighter>
                                </MKBox>
                              );
                            } else {
                              return (
                                <MKTypography
                                  component="code"
                                  variant="body2"
                                  sx={{ backgroundColor: "#f5f5f5", padding: "2px 4px", borderRadius: "4px", fontSize: "0.85em" }}
                                >
                                  {children}
                                </MKTypography>
                              );
                            }
                          },
                        },
                        pre: {
                          component: ({ children }) => {
                            const code = children.props.children.trim();
                            return (
                              <MKBox sx={{ position: "relative", mt: 2, mb: 2, backgroundColor: "rgb(47, 47, 47)"}}>
                                <CopyCodeButton code={code} onCopy={handleCopy} />
                                <pre style={{ position: "relative", padding: "0px", borderRadius: "4px", overflowX: "auto" }}>
                                  <div className="MuiBox-root css-1rd7817">
                                    <pre style={{ textAlign: "left", whiteSpace: "pre", wordSpacing: "normal", wordBreak: "normal", overflowWrap: "normal", color: "rgb(238, 238, 238)", background: "rgb(47, 47, 47)", fontFamily: "'Roboto Mono', monospace", fontSize: "0.875rem", lineHeight: "1.5em", tabSize: "4", hyphens: "none", overflow: "auto", position: "relative", margin: "0px auto", padding: "1em 1em", maxWidth: "100%", borderRadius: "4px" }}>
                                      {children.props.children}
                                    </pre>
                                  </div>
                                </pre>
                              </MKBox>
                            );
                          },
                        },
                        TwitterTweetEmbed: {
                          component: TwitterTweetEmbed,
                        },
                      },
                    }}
                  >
                    {post.content}
                  </Markdown>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Copiado al portapapeles
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(PostPage);